import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import PostStream from 'flarum/forum/components/PostStream';
import { extend } from 'flarum/common/extend';

class AiDraftPanel extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.loading = true;
    this.working = false;
    this.data = null;
    this.message = '';
    this.load();
  }
  endpoint() {
    return `${app.forum.attribute('apiUrl')}/gardensel-ai/discussions/${this.attrs.discussion.id()}/draft`;
  }
  load() {
    app
      .request({ method: 'GET', url: this.endpoint() })
      .then((payload) => {
        this.data = payload.data;
      })
      .catch(() => {
        this.data = null;
      })
      .finally(() => {
        this.loading = false;
        m.redraw();
      });
  }
  create(force = false) {
    if (this.working || !this.data?.configured) return;
    this.working = true;
    this.message = '';
    const current = this.data?.draft;
    const nonce = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    app
      .request({
        method: 'POST',
        url: this.endpoint(),
        headers: { 'Cache-Control': 'no-store', Pragma: 'no-cache' },
        body: {
          term_id: this.attrs.discussion.id(),
          draft_id: current?.id || null,
          regenerate: force,
          force_new_generation: force,
          force,
          nonce,
          previous_generation_id: current?.generation_id || null,
        },
      })
      .then((payload) => {
        if (payload?.data?.draft)
          this.data = { ...this.data, draft: payload.data.draft, history: [payload.data.draft, ...(this.data?.history || [])] };
        this.message = app.translator.trans('gardensel-ai.forum.generated');
        m.redraw();
      })
      .catch((error) => {
        this.message = error?.response?.errors?.[0]?.detail || app.translator.trans('gardensel-ai.forum.failed');
      })
      .finally(() => {
        this.working = false;
        m.redraw();
      });
  }
  review(action) {
    const draft = this.data?.draft;
    if (!draft || this.working) return;
    this.working = true;
    const critique = action === 'reject' ? window.prompt(app.translator.trans('gardensel-ai.forum.reject_reason')) : null;
    if (action === 'reject' && (!critique || !critique.trim())) {
      this.working = false;
      return;
    }
    app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/gardensel-ai/drafts/${draft.id}/${action}`,
        body: critique ? { critique } : {},
      })
      .then(() => this.load())
      .finally(() => {
        this.working = false;
        m.redraw();
      });
  }
  edit() {
    const draft = this.data?.draft;
    if (!draft || this.working) return;
    const term = window.prompt(app.translator.trans('gardensel-ai.forum.term_prompt'), draft.term);
    if (term === null) return;
    const summary = window.prompt(app.translator.trans('gardensel-ai.forum.summary_prompt'), draft.summary);
    if (summary === null) return;
    const definition = window.prompt(app.translator.trans('gardensel-ai.forum.definition_prompt'), draft.definition);
    if (definition === null) return;
    this.working = true;
    app
      .request({
        method: 'PATCH',
        url: `${app.forum.attribute('apiUrl')}/gardensel-ai/drafts/${draft.id}`,
        body: { data: { ...draft, term, summary, definition } },
      })
      .then(() => this.load())
      .finally(() => {
        this.working = false;
        m.redraw();
      });
  }
  view() {
    if (this.loading)
      return (
        <section className="GardenselAiPanel">
          <LoadingIndicator size="small" />
        </section>
      );
    const draft = this.data?.draft;
    const canReview = Boolean(this.attrs.discussion.attribute('canReviewGardenselAi'));
    return (
      <section className="GardenselAiPanel">
        <header>
          <i className="fas fa-robot" />
          <div>
            <small>{app.translator.trans('gardensel-ai.forum.kicker')}</small>
            <h3>{app.translator.trans('gardensel-ai.forum.title')}</h3>
          </div>
        </header>
        {!this.data?.configured ? <p className="GardenselAiPanel-warning">{app.translator.trans('gardensel-ai.forum.not_configured')}</p> : null}
        {draft ? (
          <div className="GardenselAiPanel-draft">
            <strong>{draft.term}</strong>
            <p>{draft.summary}</p>
            <small>{app.translator.trans(`gardensel-ai.forum.status_${draft.status}`)}</small>
            {draft.generation_id ? (
              <small className="GardenselAiPanel-generation">
                Generation: {draft.generation_id} · {draft.created_at || draft.updated_at}
              </small>
            ) : null}
            {canReview && draft.warnings?.length ? (
              <ul>
                {draft.warnings.map((item) => (
                  <li>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        {this.message ? <p>{this.message}</p> : null}
        <div className="GardenselAiPanel-actions">
          <Button
            className="Button Button--primary"
            icon="fas fa-wand-magic-sparkles"
            disabled={this.working || !this.data?.configured}
            onclick={() => (draft ? this.load() : this.create(false))}
          >
            {draft ? app.translator.trans('gardensel-ai.forum.view') : app.translator.trans('gardensel-ai.forum.generate')}
          </Button>
          {draft && canReview ? (
            <Button loading={this.working} disabled={this.working} onclick={() => this.create(true)}>
              {this.working ? app.translator.trans('gardensel-ai.forum.regenerating') : app.translator.trans('gardensel-ai.forum.regenerate')}
            </Button>
          ) : null}
          {draft && canReview && (this.data?.history?.length || 0) > 1 ? (
            <Button
              disabled={this.working}
              onclick={() => {
                const previous = this.data.history.find((item) => item.id !== draft.id);
                if (previous) {
                  this.data = { ...this.data, draft: previous };
                  m.redraw();
                }
              }}
            >
              {app.translator.trans('gardensel-ai.forum.previous')}
            </Button>
          ) : null}
          {draft && canReview ? (
            <Button disabled={this.working} onclick={() => this.edit()}>
              {app.translator.trans('gardensel-ai.forum.edit')}
            </Button>
          ) : null}
          {draft && canReview ? (
            <Button disabled={this.working} onclick={() => this.review('approve')}>
              {app.translator.trans('gardensel-ai.forum.approve')}
            </Button>
          ) : null}
          {draft && canReview ? (
            <Button disabled={this.working} onclick={() => this.review('reject')}>
              {app.translator.trans('gardensel-ai.forum.reject')}
            </Button>
          ) : null}
        </div>
      </section>
    );
  }
}

app.initializers.add('gardensel-ai', () => {
  extend(PostStream.prototype, 'afterFirstPostItems', function (items) {
    const discussion = this.discussion;
    if (!discussion || !discussion.attribute('canGenerateGardenselAi')) return;
    items.add(
      'gardenselAiDraft',
      <AiDraftPanel key="gardenselAiDraft" discussion={discussion} />,
      5
    );
  });
});

export default {};
